import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
    Menu,
    X,
    ArrowUpRight,
    CircuitBoard,
    Phone,
    Sparkles,
} from "lucide-react";
import { navLinks, company } from "@/data/landing";

const Header = () => {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const [active, setActive] = useState("");

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 24);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    // Track active section via IntersectionObserver
    useEffect(() => {
        const ids = navLinks.map((l) => l.href.replace("#", ""));
        const sections = ids
            .map((id) => document.getElementById(id))
            .filter(Boolean);
        if (!sections.length) return;
        const io = new IntersectionObserver(
            (entries) => {
                entries.forEach((e) => {
                    if (e.isIntersecting) setActive(`#${e.target.id}`);
                });
            },
            { rootMargin: "-40% 0px -55% 0px", threshold: 0 }
        );
        sections.forEach((s) => io.observe(s));
        return () => io.disconnect();
    }, []);

    return (
        <motion.header
            initial={{ y: -80, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            data-testid="site-header"
            className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
                scrolled
                    ? "bg-black/70 backdrop-blur-xl border-b border-white/10"
                    : "bg-transparent"
            }`}
        >
            {/* subtle gradient underline when scrolled */}
            {scrolled && (
                <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-cyan-400/30 to-transparent" />
            )}

            <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 md:h-[72px] flex items-center justify-between">
                {/* Logo */}
                <a
                    href="#hero"
                    data-testid="brand-logo"
                    className="group flex items-center gap-3"
                    data-cursor="hover"
                >
                    <span className="relative inline-flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-cyan-400/20 to-violet-500/20 border border-white/10 overflow-hidden">
                        <motion.span
                            aria-hidden
                            className="absolute inset-0 bg-gradient-to-br from-cyan-400/40 to-violet-500/40 opacity-0 group-hover:opacity-100 transition"
                        />
                        <motion.div
                            animate={{ rotate: 360 }}
                            transition={{ duration: 16, repeat: Infinity, ease: "linear" }}
                        >
                            <CircuitBoard className="h-5 w-5 text-cyan-300" strokeWidth={1.75} />
                        </motion.div>
                        <span className="absolute -inset-2 rounded-full bg-cyan-400/20 blur-md opacity-0 group-hover:opacity-100 transition" />
                    </span>
                    <div className="flex flex-col leading-tight">
                        <span className="font-display font-bold text-white text-base tracking-tight">
                            Atvicon
                        </span>
                        <span className="font-mono text-[9px] uppercase tracking-[0.28em] text-zinc-500">
                            Technologies
                        </span>
                    </div>
                </a>

                {/* Center nav */}
                <nav className="hidden lg:flex items-center gap-1 glass rounded-full px-2 py-1.5 border border-white/10">
                    {navLinks.map((link) => {
                        const isActive = active === link.href;
                        return (
                            <a
                                key={link.href}
                                href={link.href}
                                data-testid={`nav-link-${link.label.toLowerCase()}`}
                                data-cursor="hover"
                                className={`relative px-4 py-2 text-sm font-medium rounded-full transition-colors ${
                                    isActive
                                        ? "text-black"
                                        : "text-zinc-400 hover:text-white"
                                }`}
                            >
                                {isActive && (
                                    <motion.span
                                        layoutId="nav-pill"
                                        className="absolute inset-0 rounded-full bg-cyan-400"
                                        transition={{
                                            type: "spring",
                                            stiffness: 380,
                                            damping: 30,
                                        }}
                                    />
                                )}
                                <span className="relative">{link.label}</span>
                            </a>
                        );
                    })}
                </nav>

                {/* Right side */}
                <div className="hidden lg:flex items-center gap-3">
                    <a
                        href={`tel:${company.phone.replace(/\s/g, "")}`}
                        data-testid="header-phone"
                        data-cursor="hover"
                        className="group flex items-center gap-2 text-zinc-400 hover:text-white transition"
                    >
                        <span className="relative inline-flex h-2 w-2">
                            <span className="absolute inset-0 rounded-full bg-cyan-400 animate-ping opacity-75" />
                            <span className="relative inline-flex h-2 w-2 rounded-full bg-cyan-300" />
                        </span>
                        <span className="font-mono text-xs tracking-wide">
                            {company.phone}
                        </span>
                    </a>
                    <a
                        href="#contact"
                        data-testid="header-cta"
                        data-cursor="hover"
                        className="btn-shine relative inline-flex items-center gap-2 rounded-full bg-cyan-400 hover:bg-cyan-300 text-black font-semibold text-sm px-5 py-2.5 transition shadow-[0_0_30px_-10px_rgba(0,229,255,0.6)]"
                    >
                        <Sparkles className="h-3.5 w-3.5" />
                        Start a Project
                        <ArrowUpRight className="h-3.5 w-3.5" />
                    </a>
                </div>

                {/* Mobile toggle */}
                <button
                    onClick={() => setOpen((o) => !o)}
                    data-testid="mobile-menu-toggle"
                    aria-label="Toggle menu"
                    className="lg:hidden h-10 w-10 grid place-items-center rounded-lg border border-white/10 text-white bg-white/[0.04] hover:bg-white/[0.08] transition"
                >
                    <AnimatePresence mode="wait">
                        {open ? (
                            <motion.div
                                key="x"
                                initial={{ rotate: -90, opacity: 0 }}
                                animate={{ rotate: 0, opacity: 1 }}
                                exit={{ rotate: 90, opacity: 0 }}
                                transition={{ duration: 0.2 }}
                            >
                                <X className="h-5 w-5" />
                            </motion.div>
                        ) : (
                            <motion.div
                                key="menu"
                                initial={{ rotate: 90, opacity: 0 }}
                                animate={{ rotate: 0, opacity: 1 }}
                                exit={{ rotate: -90, opacity: 0 }}
                                transition={{ duration: 0.2 }}
                            >
                                <Menu className="h-5 w-5" />
                            </motion.div>
                        )}
                    </AnimatePresence>
                </button>
            </div>

            {/* Mobile menu */}
            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        className="lg:hidden bg-black/95 backdrop-blur-xl border-t border-white/10 overflow-hidden"
                        data-testid="mobile-menu"
                    >
                        <div className="px-6 py-6 flex flex-col gap-1">
                            {navLinks.map((link, i) => (
                                <motion.a
                                    key={link.href}
                                    initial={{ x: -20, opacity: 0 }}
                                    animate={{ x: 0, opacity: 1 }}
                                    transition={{ delay: i * 0.05 }}
                                    href={link.href}
                                    onClick={() => setOpen(false)}
                                    data-testid={`mobile-nav-${link.label.toLowerCase()}`}
                                    className="flex items-center justify-between px-3 py-4 text-zinc-300 hover:text-white border-b border-white/5"
                                >
                                    <span>{link.label}</span>
                                    <ArrowUpRight className="h-4 w-4 text-zinc-600" />
                                </motion.a>
                            ))}
                            <a
                                href={`tel:${company.phone.replace(/\s/g, "")}`}
                                className="mt-4 flex items-center gap-3 px-3 py-3 text-zinc-300"
                            >
                                <Phone className="h-4 w-4 text-cyan-300" />
                                <span className="font-mono text-sm">{company.phone}</span>
                            </a>
                            <a
                                href="#contact"
                                onClick={() => setOpen(false)}
                                className="mt-2 inline-flex items-center justify-center gap-2 rounded-lg bg-cyan-400 text-black font-semibold py-3.5"
                            >
                                <Sparkles className="h-4 w-4" />
                                Start a Project
                                <ArrowUpRight className="h-4 w-4" />
                            </a>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </motion.header>
    );
};

export default Header;
